// UNSAFE JWT decoder — base64url decode of the payload only.
//
// Lives in its own file (separate from `utils/jwt-verify.ts` which
// owns `verifyJwt` and pulls in `jose`). Two reasons for the split:
//
//   1. Make it harder to accidentally use the unsafe variant in a
//      security-sensitive context. The import path itself reads
//      `jwt-unsafe`.
//   2. Avoid pulling jose (ESM-only) into modules that jest's CJS
//      transformer can't parse (e.g. `pages/_app.tsx`). This file
//      has zero deps and works under any module loader.
//
// The returned object MUST NOT be used to gate any authorization
// decision — call sites must assume an attacker can put anything in
// any field.
//
// Permitted uses (non-trust-boundary):
//   - Redirect hints where the alternative is letting the user reach
//     a screen that will then require a verified credential.
//
// Forbidden uses:
//   - As the user_id for any server-side mutation (e.g. Auth0 Mgmt
//     PATCH). Use `verifyJwt` from `utils/jwt-verify.ts` instead and
//     trust `payload.sub` only after verification succeeds.

export interface DecodedJwt {
  sub?: string;
  email?: string;
  app_metadata?: Record<string, unknown>;
  [k: string]: unknown;
}

export const unsafeDecodeJwtPayload = (token: string): DecodedJwt | null => {
  if (typeof token !== 'string' || token.length === 0) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;

  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json = Buffer.from(padded, 'base64').toString('utf8');
    return JSON.parse(json) as DecodedJwt;
  } catch {
    return null;
  }
};
